import { useContext } from 'react'
import { BuilderContext } from '../BuilderContextInstance'

/**
 * Cadence d'écriture des `DataSync` des blocs — `plugins.hook.useDataSyncPolicy`
 * du cœur reactive-json (≥ 2.12), enregistré dans builderPlugins SEULEMENT : les
 * écrans de l'application gardent le comportement du cœur.
 *
 * Pourquoi du JS et pas du YAML : la cadence est une propriété de l'HÔTE (une
 * session d'édition du builder), lue dans un contexte React (`BuilderContext` :
 * `autoSave`, `flushVersion`, `markContentDirty`, `trackSync`) qu'aucun rjBuild
 * ne peut lire ; le même YAML de bloc est monté par six hôtes (éditeur, éditeur
 * d'objet, tableaux de bord, pages intégrées, site public, îlots SSG). Le cœur
 * prévoit ce point d'extension exactement pour ça.
 *
 * Ce que la politique décide :
 *   - `manual` : dans le builder, l'enregistrement est toujours manuel
 *     (`autoSave` vaut `false` dans le contexte). Une modification est RETENUE
 *     (signalée par `markContentDirty` → bouton Enregistrer + garde de sortie de
 *     page) et n'est écrite qu'au bouton Enregistrer, qui incrémente
 *     `flushVersion`. Hors builder (aucun BuilderContext) : écriture au repos,
 *     comme le cœur ;
 *   - `onSyncStart` : chaque écriture qui démarre remet sa promesse à
 *     `trackSync`, et le Save (`flushAll`) attend toutes celles en cours, dont
 *     les siennes. Une version créée juste après — un instantané lu en base —
 *     contient donc l'édition ;
 *   - `defaultMergeKey` : sans `mergeKey` déclaré, deux montages de la MÊME
 *     entité (bloc lié posé deux fois, fragment embarqué plusieurs fois) forment
 *     un groupe sur leur `item_url` — miroir en direct, un seul propriétaire qui
 *     écrit. Évite d'ajouter `mergeKey:` à chaque définition de bloc.
 *
 * Limite connue : `BuilderProvider` est aussi monté par les hôtes en LECTURE
 * (site public, îlots SSG, tableaux de bord) avec `autoSave: false`, donc une
 * modification y serait retenue sans bouton pour l'écrire. Sans effet
 * aujourd'hui (le contenu d'une unité ne se modifie que dans l'éditeur), mais un
 * bloc qui synchroniserait une autre entité (ex. `ContactPageBlock`,
 * `~~.contact`) y resterait bloqué. Correctif prévu : `manual` seulement pour
 * `path === 'apiData'` en mode édition.
 */
export const useDataSyncPolicy = () => {
  const builder = useContext(BuilderContext)
  return {
    manual: builder ? !builder.autoSave : false,
    flushSignal: builder?.flushVersion ?? 0,
    onDirty: builder?.markContentDirty,
    onSyncStart: builder?.trackSync,
    defaultMergeKey: (syncable) => syncable?.item_url ?? null,
  }
}
